
import { useCallback, useRef } from "react";
import { User } from "@supabase/supabase-js";
import { Profile } from "@/services/profiles";
import { updateUserProfile as updateProfileService } from "../authHelpers";
import { forceSessionRefresh } from "@/services/auth/sessionUtils";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger('useProfileManagement');

export const useProfileManagement = (
  user: User | null,
  setProfile: (profile: Profile) => void,
  setIsLoading: (isLoading: boolean) => void
) => {
  const refreshInProgress = useRef(false);

  // Refresh the user's profile
  const refreshProfile = useCallback(async () => {
    if (!user || refreshInProgress.current) {
      return;
    }
    
    refreshInProgress.current = true;
    setIsLoading(true);
    
    try {
      logger.info("Manually refreshing profile for user:", user.id);
      const profileData = await updateProfileService(user.id, {});
      if (profileData) {
        logger.info("Profile refresh successful:", profileData);
        setProfile(profileData);
      } else {
        logger.warn("Profile refresh returned no data");
        
        if (await forceSessionRefresh()) {
          const retryProfileData = await updateProfileService(user.id, {});
          if (retryProfileData) {
            logger.info("Profile refresh successful after session refresh:", retryProfileData);
            setProfile(retryProfileData);
          }
        }
      }
    } catch (error) {
      logger.error("Error refreshing profile:", error);
    } finally {
      setIsLoading(false);
      refreshInProgress.current = false;
    }
  }, [user, setIsLoading, setProfile]);

  // Update user profile
  const updateUserProfile = useCallback(
    async (updates: Partial<Profile>) => {
      if (!user) {
        logger.warn("Cannot update profile: no authenticated user");
        return null;
      }
      
      logger.info("Updating profile for user:", user.id, "with data:", updates);
      const updatedProfile = await updateProfileService(user.id, updates);
      if (updatedProfile) {
        logger.info("Profile update successful:", updatedProfile);
        setProfile(updatedProfile);
      } else {
        logger.warn("Profile update returned no data");
      }
      return updatedProfile;
    },
    [user, setProfile]
  );

  return {
    refreshProfile,
    updateUserProfile
  };
};
